/**
 * EventLightbox
 * Modal shown automatically when a logged-in member has a pending
 * event invitation they haven't responded to yet.
 *
 * Shows: cover image/YouTube preview, title, date/time, location,
 *        organiser, description, and RSVP buttons (Ja / Nee / Misschien).
 * Dismissable with ✕ — the item stays in the inbox for later response.
 */
import React, { useState } from 'react';
import { db } from '../../../firebase';
import {
  doc,
  updateDoc,
  setDoc,
  serverTimestamp,
  increment,
} from 'firebase/firestore';

interface EventData {
  title: string;
  description?: string;
  date: string;
  startTime: string;
  endTime?: string | null;
  locationType?: 'physical' | 'online' | 'hybrid';
  locationAddress?: string | null;
  onlineLink?: string | null;
  coverImageUrl?: string | null;
  youtubeUrl?: string | null;
  maxAttendees?: number | null;
  rsvpDeadline?: string | null;
  createdByName?: string;
}

interface EventLightboxProps {
  inboxItemId: string;
  eventId: string;
  clubId?: string;
  entityId?: string;
  clubName?: string;
  eventData: EventData;
  userId: string;
  onDismiss: () => void;
  onResponded: () => void;
}

const EventLightbox: React.FC<EventLightboxProps> = ({
  inboxItemId,
  eventId,
  clubId,
  entityId,
  clubName,
  eventData,
  userId,
  onDismiss,
  onResponded,
}) => {
  const [responding, setResponding] = useState(false);
  const [responded, setResponded] = useState<'yes' | 'no' | 'maybe' | null>(null);

  /* ── Format helpers ─────────────────────────────────────────── */
  const dateLabel = eventData.date
    ? new Intl.DateTimeFormat('nl-NL', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      }).format(new Date(eventData.date))
    : '';

  const timeLabel = eventData.startTime
    ? eventData.endTime
      ? `${eventData.startTime} – ${eventData.endTime}`
      : `${eventData.startTime}`
    : '';

  const locationText =
    eventData.locationType === 'online'
      ? eventData.onlineLink || 'Online'
      : eventData.locationType === 'hybrid'
      ? [eventData.locationAddress, eventData.onlineLink].filter(Boolean).join(' / ')
      : eventData.locationAddress || '';

  /* ── Extract YouTube video ID ───────────────────────────────── */
  const getYouTubeId = (url: string) => {
    const m = url.match(
      /(?:youtube\.com\/(?:[^/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?/\s]{11})/
    );
    return m ? m[1] : null;
  };
  const youtubeId = eventData.youtubeUrl ? getYouTubeId(eventData.youtubeUrl) : null;

  /* ── RSVP handler ───────────────────────────────────────────── */
  const handleRSVP = async (response: 'yes' | 'no' | 'maybe') => {
    if (responding) return;
    setResponding(true);
    try {
      // Mark inbox item as read + store RSVP response
      await updateDoc(doc(db, 'inboxItems', inboxItemId), {
        rsvpResponse: response,
        isRead: true,
        respondedAt: serverTimestamp(),
      });

      // Upsert eventRSVPs document
      if (eventId) {
        await setDoc(
          doc(db, 'eventRSVPs', `${eventId}_${userId}`),
          {
            eventId,
            entityId: entityId || null,
            clubId: clubId || null,
            userId,
            response,
            respondedAt: serverTimestamp(),
          },
          { merge: true }
        );

        // Increment counter on the clubEvents document
        const field =
          response === 'yes' ? 'rsvpYes' : response === 'no' ? 'rsvpNo' : 'rsvpMaybe';
        try {
          await updateDoc(doc(db, 'clubEvents', eventId), {
            [field]: increment(1),
          });
        } catch {}
      }

      setResponded(response);
      setTimeout(onResponded, 1400);
    } catch (e) {
      console.error('RSVP failed:', e);
    } finally {
      setResponding(false);
    }
  };

  const responseEmoji = { yes: '✅', no: '❌', maybe: '🤔' };
  const responseLabel = { yes: 'Aanwezig!', no: 'Niet aanwezig', maybe: 'Misschien aanwezig' };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[80] flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden">
        {/* ── Cover ─────────────────────────────────────────────── */}
        {youtubeId ? (
          <div className="relative w-full" style={{ paddingBottom: '42%' }}>
            <iframe
              src={`https://www.youtube.com/embed/${youtubeId}?autoplay=0&controls=1&rel=0&modestbranding=1`}
              title="Event video"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="absolute inset-0 w-full h-full"
            />
          </div>
        ) : eventData.coverImageUrl ? (
          <img
            src={eventData.coverImageUrl}
            alt={eventData.title}
            className="w-full h-36 object-cover"
          />
        ) : (
          <div
            className="w-full h-20 flex items-center justify-center text-3xl"
            style={{ background: 'linear-gradient(135deg, #7A9488 0%, #4F6660 100%)' }}
          >
            📅
          </div>
        )}

        {/* ── Dismiss button ────────────────────────────────────── */}
        <button
          onClick={onDismiss}
          aria-label="Sluiten"
          className="absolute top-3 right-3 w-7 h-7 bg-black/40 hover:bg-black/60 text-white rounded-full flex items-center justify-center text-xs font-bold transition-colors z-10"
          style={{ position: 'absolute' }}
        >
          ✕
        </button>

        {/* ── Body ──────────────────────────────────────────────── */}
        <div className="p-5 space-y-3">
          {/* Club tag */}
          {clubName && (
            <p className="text-[10px] font-semibold uppercase tracking-widest" style={{ color: '#7A9488' }}>
              {clubName}
            </p>
          )}

          {/* Title */}
          <h2 className="text-lg font-bold text-gray-900 leading-tight">{eventData.title}</h2>

          {/* Date / time / location */}
          <div className="space-y-1 text-xs text-gray-600">
            {dateLabel && (
              <div className="flex items-center gap-1.5">
                <span>📅</span>
                <span>{dateLabel}{timeLabel ? ` · ${timeLabel}` : ''}</span>
              </div>
            )}
            {locationText && (
              <div className="flex items-center gap-1.5">
                <span>{eventData.locationType === 'online' ? '💻' : '📍'}</span>
                <span className="truncate">{locationText}</span>
              </div>
            )}
            {eventData.createdByName && (
              <div className="flex items-center gap-1.5">
                <span>👤</span>
                <span>Georganiseerd door {eventData.createdByName}</span>
              </div>
            )}
            {eventData.rsvpDeadline && (
              <div className="flex items-center gap-1.5 text-amber-600 font-medium">
                <span>⏰</span>
                <span>
                  Reageer voor{' '}
                  {new Intl.DateTimeFormat('nl-NL', { day: 'numeric', month: 'long' }).format(
                    new Date(eventData.rsvpDeadline)
                  )}
                </span>
              </div>
            )}
            {eventData.maxAttendees && (
              <div className="flex items-center gap-1.5 text-gray-500">
                <span>👥</span>
                <span>Max {eventData.maxAttendees} deelnemers</span>
              </div>
            )}
          </div>

          {/* Description */}
          {eventData.description && (
            <p className="text-xs text-gray-600 leading-relaxed line-clamp-3">
              {eventData.description}
            </p>
          )}

          {/* RSVP buttons or confirmation */}
          {responded ? (
            <div className="text-center py-2">
              <div className="text-2xl mb-1">{responseEmoji[responded]}</div>
              <p className="font-semibold text-gray-700 text-sm">{responseLabel[responded]}</p>
              <p className="text-xs text-gray-500 mt-0.5">Je reactie is opgeslagen in je inbox.</p>
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-2 pt-1">
              <button
                onClick={() => handleRSVP('yes')}
                disabled={responding}
                className="flex flex-col items-center gap-1 py-2.5 rounded-xl border-2 text-xs font-semibold transition-all disabled:opacity-50 hover:border-green-400 hover:bg-green-50 border-gray-200"
              >
                <span className="text-xl">✅</span>
                <span className="text-green-700">Ja</span>
              </button>
              <button
                onClick={() => handleRSVP('maybe')}
                disabled={responding}
                className="flex flex-col items-center gap-1 py-2.5 rounded-xl border-2 text-xs font-semibold transition-all disabled:opacity-50 hover:border-amber-400 hover:bg-amber-50 border-gray-200"
              >
                <span className="text-xl">🤔</span>
                <span className="text-amber-700">Misschien</span>
              </button>
              <button
                onClick={() => handleRSVP('no')}
                disabled={responding}
                className="flex flex-col items-center gap-1 py-2.5 rounded-xl border-2 text-xs font-semibold transition-all disabled:opacity-50 hover:border-red-300 hover:bg-red-50 border-gray-200"
              >
                <span className="text-xl">❌</span>
                <span className="text-red-600">Nee</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EventLightbox;
